import { Form, Input, Select } from "antd";

import { getInitialOption } from "shared/lib/getInitialOption";
import { toKebab } from "shared/lib/toKebab";
import RemoteHandbookSelect from "features/handbook/ui/RemoteHandbookSelect";
import { RichTextEditor } from "shared/ui/richText/RichTextEditor";

type BaseProps = {
  name: string;
  label: string;
  /** Текст ошибки, если обязательное поле не заполнено. */
  message: string;
  /** Обязательность берётся из swagger-схемы справочника. */
  required?: boolean;
};

/** Правило «заполните поле» ставим только там, где этого требует бэкенд. */
const requiredRule = (required: boolean | undefined, message: string) =>
  required ? [{ required: true, message }] : [];

type TextFieldProps = BaseProps & {
  readOnly?: boolean;
  disabled?: boolean;
  type?: string;
  min?: number;
  step?: number | string;
};

/** Текстовое поле — самый частый вид поля в справочниках. */
export const TextField = ({
  name,
  label,
  message,
  required,
  ...inputProps
}: TextFieldProps) => (
  <Form.Item name={name} label={label} rules={requiredRule(required, message)}>
    <Input allowClear {...inputProps} />
  </Form.Item>
);

type SelectFieldProps = BaseProps & {
  placeholder: string;
  options: Array<{ value: string; label: string }>;
};

/** Выбор из фиксированного списка значений. */
export const SelectField = ({
  name,
  label,
  message,
  required,
  placeholder,
  options,
}: SelectFieldProps) => (
  <Form.Item name={name} label={label} rules={requiredRule(required, message)}>
    <Select
      allowClear
      showSearch
      placeholder={placeholder}
      options={options}
      optionFilterProp="label"
    />
  </Form.Item>
);

type RefFieldProps = BaseProps & {
  /** Имя поля-ссылки в данных; из него же выводится адрес справочника. */
  fieldKey: string;
  initial: any;
  language: "ru" | "ky";
};

/** Ссылка на другой справочник с подгрузкой вариантов по мере скролла. */
export const HandbookRefField = ({
  name,
  label,
  message,
  required,
  fieldKey,
  initial,
  language,
}: RefFieldProps) => (
  <Form.Item name={name} label={label} rules={requiredRule(required, message)}>
    <RemoteHandbookSelect
      slug={toKebab(fieldKey)}
      language={language}
      initialOption={getInitialOption(fieldKey, initial, language)}
    />
  </Form.Item>
);

/** Пустой HTML (`<p></p>`, `&nbsp;`) визуально выглядит заполненным — проверяем текст. */
const requireNonEmptyHtml = {
  validator: async (_: unknown, value: string) => {
    const text = (value || "")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim();
    if (!text) throw new Error("Укажите описание");
  },
};

/** Описание: у агрокредитов — с форматированием, у остальных — обычная строка. */
export const DescriptionField = ({
  name,
  label,
  rich,
  required,
}: {
  name: string;
  label: string;
  rich?: boolean;
  required?: boolean;
}) =>
  rich ? (
    <Form.Item
      name={name}
      label={label}
      rules={
        required
          ? [
              { required: true, message: "Укажите описание" },
              requireNonEmptyHtml,
            ]
          : []
      }
    >
      <RichTextEditor placeholder="Введите описание…" />
    </Form.Item>
  ) : (
    <TextField
      name={name}
      label={label}
      message="Укажите описание"
      required={required}
    />
  );
